import { IInfluencerBooking, IInfluencerCards, UserWorkoutSession, WaitlistEntry } from 'fitbeat.gym-core';
import produce from 'immer';
import moment from 'moment-timezone';
import {
  ACCEPT_INVITE,
  ACCEPT_WORKOUT_SETTINGS_CHANGE,
  BOOKING_INVITE_CONTINUE,
  CANCEL_BOOKING_CONFIRMED,
  CHANGE_BOOKING_SUCCESS,
  CHECKIN_SUCCESS,
  CLOSE_FEATURE_DEMO_SCREEN_FAILURE,
  CLOSE_FEATURE_DEMO_SCREEN_REQUEST,
  CLOSE_FEATURE_DEMO_SCREEN_SUCCESS,
  CONFIRM_REMOVE_WAITLIST_ENTRY,
  MAKE_BOOKING_SUCCESS, START_ON_DEMAND_STUDIO_BOOKING_SUCCESS, UPDATING_BOXING_SETTINGS_DURING_CHECKIN_SUCCESS,
  WAITINGLIST_SUCCESS,
} from '../Bookings/actionTypes';
import {
  COMPLETE_WORKOUT_REQUEST,
  PRE_WORKOUT_CANCEL_FAILURE,
  PRE_WORKOUT_CANCEL_REQUEST,
  PRE_WORKOUT_CANCEL_SUCCESS,
  PRE_WORKOUT_SCREEN_CLOSE_FAILURE,
  PRE_WORKOUT_SCREEN_CLOSE_REQUEST,
  PRE_WORKOUT_SCREEN_CLOSE_SUCCESS,
} from '../HomeWorkouts';
import { GymResolver } from '../services';
import {
  DASHBOARD_OPENED_FAILURE,
  DASHBOARD_OPENED_REQUEST,
  DASHBOARD_OPENED_SUCCESS,
} from '../SummaryDashboard/actionTypes';
import {
  INFLUENCER_SCHEDULE_TILE_PRESSED,
  INVITE_SCREEN_UNMOUNTED,
  PARTNER_BOOKING_UPDATED,
  PLANNER_OPENED,
  PLANNER_OPENED_FAILED,
  PLANNER_OPENED_REQUEST,
  PLANNER_OPENED_SUCCESS,
  PlannerActionTypes,
  STUDIO_PARTNER_INVITE_ACCEPTED,
  STUDIO_PARTNER_INVITE_CHANGED,
  STUDIO_PARTNER_INVITE_REJECTED,
} from './plannerActions';
import * as actionTypes from '../auth/actionTypes';

export interface IPlannerState {
  plannerError: Error | null;
  days: moment.Moment[];
  workoutSessions: UserWorkoutSession[];
  waitlistEntries: WaitlistEntry[];
  marketingTiles: IInfluencerCards[];
  influencerBookings: IInfluencerBooking[];
  requestInProgress: boolean;
  fetchingWorkoutSessions: boolean;
  workoutInviteStartTimeForInfluencerBooking?: number;
  influencerWorkoutInviteId?: string;
  referredByFullnameForInfluencer?: string;
}

const initialPlannerState: IPlannerState = {
  plannerError: null,
  days: [],
  workoutSessions: [],
  waitlistEntries: [],
  marketingTiles: [],
  influencerBookings: [],
  requestInProgress: false,
  fetchingWorkoutSessions: false,
};

export const plannerReducer = produce(
  (draft: IPlannerState = initialPlannerState, action: PlannerActionTypes): IPlannerState => {
    switch (action.type) {
      case PLANNER_OPENED:
        draft.days = action.payload.days;
        break;
      case STUDIO_PARTNER_INVITE_ACCEPTED:
        removeSessionFromAllSessions(draft, action.payload.acceptedInvite.uniqueReference);
        draft.workoutSessions.push(action.payload.acceptedInvite);
        break;
      case STUDIO_PARTNER_INVITE_REJECTED:
        removeSessionFromAllSessions(draft, action.payload.inviteId);
        break;
      case PLANNER_OPENED_REQUEST:
        draft.requestInProgress = true;
        break;
      case PLANNER_OPENED_SUCCESS:
        draft.plannerError = null;
        draft.requestInProgress = false;
        setWorkoutSessions(draft, action.payload.bookings);
        break;
      case PLANNER_OPENED_FAILED:
        draft.requestInProgress = false;
        draft.plannerError = action.payload.error;
        break;
      case MAKE_BOOKING_SUCCESS:
        setWorkoutSessions(draft, action.payload.workoutSessions);

        if (!UserWorkoutSession.isHomeBooking(action.payload.bookingJustCreated, GymResolver.getByName)) {
          draft.waitlistEntries = action.payload.waitlistEntries;
        }
        break;
      case CHECKIN_SUCCESS:
        removeSessionFromAllSessions(draft, action.payload.changedWorkoutId);
        draft.workoutSessions.push(action.payload.workoutSession);
        break;
      case CHANGE_BOOKING_SUCCESS:
        removeSessionFromAllSessions(draft, action.payload.changedWorkoutId);
        draft.workoutSessions.push(action.payload.workoutSession);
        break;
      case STUDIO_PARTNER_INVITE_CHANGED:
        removeSessionFromAllSessions(draft, action.payload.workoutId);
        draft.workoutSessions.push(action.payload.changedWorkout);
        break;
      case PARTNER_BOOKING_UPDATED:
        removeSessionFromAllSessions(draft, action.payload.workout.uniqueReference);
        draft.workoutSessions.push(action.payload.workout);
        break;
      case CANCEL_BOOKING_CONFIRMED:
        setWorkoutSessions(draft, action.payload.workoutSessions);
        break;
      case DASHBOARD_OPENED_REQUEST:
        draft.requestInProgress = true;
        break;
      case DASHBOARD_OPENED_SUCCESS:
        if (action.payload.allWorkoutSessions) {
          draft.workoutSessions = action.payload.allWorkoutSessions;
        }
        if (action.payload.waitlistEntries) {
          draft.waitlistEntries = action.payload.waitlistEntries;
        }
        if (action.payload.marketingTiles) {
          draft.marketingTiles = action.payload.marketingTiles;
        }
        if (action.payload.influencerBookings) {
          draft.influencerBookings = action.payload.influencerBookings;
        }
        break;
      case DASHBOARD_OPENED_FAILURE:
        draft.requestInProgress = false;
        break;
      case BOOKING_INVITE_CONTINUE:
        draft.workoutSessions = draft.workoutSessions.map((session: UserWorkoutSession) => {
          if (session.uniqueReference === action.payload.workoutSessionId) {
            session.openInviteLink = action.payload.shareLink;
          }
          return session;
        });
        break;
      case ACCEPT_INVITE:
        if (action.payload.userWorkoutSession) {
          draft.workoutSessions.push(action.payload.userWorkoutSession);
        }
        break;
      case ACCEPT_WORKOUT_SETTINGS_CHANGE:
        setWorkoutSessions(draft, action.payload.allWorkoutSessions);
        break;
      case PRE_WORKOUT_SCREEN_CLOSE_REQUEST:
        if (action.payload.deleteBookingId) {
          removeSessionFromAllSessions(draft, action.payload.deleteBookingId);
        }
        draft.fetchingWorkoutSessions = true;
        break;
      case PRE_WORKOUT_SCREEN_CLOSE_SUCCESS:
        draft.fetchingWorkoutSessions = false;
        break;
      case PRE_WORKOUT_SCREEN_CLOSE_FAILURE:
        draft.fetchingWorkoutSessions = false;
        break;
      case PRE_WORKOUT_CANCEL_REQUEST:
        if (action.payload.deleteBookingId) {
          removeSessionFromAllSessions(draft, action.payload.deleteBookingId);
        }
        draft.fetchingWorkoutSessions = true;
        break;
      case PRE_WORKOUT_CANCEL_SUCCESS:
        draft.fetchingWorkoutSessions = false;
        break;
      case PRE_WORKOUT_CANCEL_FAILURE:
        draft.fetchingWorkoutSessions = false;
        break;
      case COMPLETE_WORKOUT_REQUEST:
        if (action.payload.workoutId) {
          removeSessionFromAllSessions(draft, action.payload.workoutId);
        }
        break;
      case CLOSE_FEATURE_DEMO_SCREEN_REQUEST:
        if (action.payload.deleteBookingId) {
          removeSessionFromAllSessions(draft, action.payload.deleteBookingId);
        }
        draft.fetchingWorkoutSessions = true;
        break;
      case CLOSE_FEATURE_DEMO_SCREEN_SUCCESS:
        draft.fetchingWorkoutSessions = false;
        break;
      case CLOSE_FEATURE_DEMO_SCREEN_FAILURE:
        draft.fetchingWorkoutSessions = false;
        break;
      case CONFIRM_REMOVE_WAITLIST_ENTRY:
        const index = draft.waitlistEntries.findIndex(
          (waitlistEntry) => waitlistEntry.uid === action.payload.waitlistEntry.uid);
        draft.waitlistEntries.splice(index, 1);
        break;
      case WAITINGLIST_SUCCESS:
        draft.waitlistEntries = action.payload.waitlistEntries;
        break;
      case START_ON_DEMAND_STUDIO_BOOKING_SUCCESS:
        draft.workoutSessions.push(action.payload.booking);
        break;
      case INFLUENCER_SCHEDULE_TILE_PRESSED: {
        draft.workoutInviteStartTimeForInfluencerBooking = action.payload.bookingTime.toString();
        draft.influencerWorkoutInviteId = action.payload.inviteId;
        draft.referredByFullnameForInfluencer = action.payload.referredByFullname;
        break;
      }
      case INVITE_SCREEN_UNMOUNTED: {
        draft.influencerWorkoutInviteId = undefined;
        draft.workoutInviteStartTimeForInfluencerBooking = undefined;
        draft.referredByFullnameForInfluencer = undefined;
        break;
      }
      case UPDATING_BOXING_SETTINGS_DURING_CHECKIN_SUCCESS:
        updateWorkoutSession(draft, action.payload.updatedWorkout);
        break;
    }
    return draft;
  });

function removeSessionFromAllSessions(draft: IPlannerState, workoutIdToRemove: any) {
  const indexToRemove = draft.workoutSessions.findIndex((session: UserWorkoutSession) => (
    session.uniqueReference === workoutIdToRemove
  ));
  const numberOfItemsToRemove = 1;
  draft.workoutSessions.splice(indexToRemove, numberOfItemsToRemove);
}

function setWorkoutSessions(draft: IPlannerState, workoutSessions?: UserWorkoutSession[]) {
  if (workoutSessions) {
    draft.workoutSessions = workoutSessions;
  }
}

function updateWorkoutSession(draft: IPlannerState, updatedWorkout?: UserWorkoutSession) {
  if (!updatedWorkout) {
    return;
  }
  draft.workoutSessions = draft.workoutSessions.map((session) => {
    if (session.uniqueReference === updatedWorkout.uniqueReference) {
      return updatedWorkout;
    }
    return session;
  });
}
